import { __decorate } from "tslib";
import { LitElement, html, css } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { elements } from './sdb-elements.js';
import '@polymer/iron-image/iron-image.js';
/**
 * Imagecard element, used for visual information sections like a dashboard tile
 * Normally used to display an image in the top section and wrap a listitem element in the bottom section
 *
 * @prop {String} img - used to define an image to be used as background
 * @prop {String} placeholder - used to define an image to be used as placeholder
 * @prop {Boolean} noOverlay - used to remove the overlay effect (for clear image)

 * @prop {Boolean} secondary - used to set the secondary background color
 * @prop {Boolean} tertiary - used to set the tertiary background color
 * @prop {String} background - used to set a manual background color

 * @prop {Boolean} noHover - used to remove the hover effect
 * @slot top - This slot is used to hold items in the top section of the imagecard element
 * @slot bottom - This slot is used to hold items in the bottom section of the imagecard element
 *  (normally listitems elements)
 *
 */
let SDBContentImageCard = class SDBContentImageCard extends LitElement {
    constructor() {
        super(...arguments);
        this.img = '';
        this.placeholder = '';
        this.background = '';
        this.secondary = false;
        this.tertiary = false;
        this.hover = false;
        this.noOverlay = false;
    }
    render() {
        return html `
            <section
                id="imageWrapper"
                style="background-color:${this.background}"
            >
                <iron-image
                    class="backgroundImage"
                    sizing="cover"
                    fade
                    preload
                    src="${this.img}"
                    placeholder="${this.placeholder}"
                ></iron-image>
                <slot class="image" name="top"></slot>
                ${this.img || this.placeholder
            ? html `<div
                          class="overlay"
                          style="background-color:${this.background}"
                      ></div> `
            : html ``}
            </section>
            <section id="contentWrapper">
                <slot name="bottom"></slot>
            </section>
        `;
    }
};
SDBContentImageCard.styles = [
    elements,
    css `
            :host {
                background: var(--sdb-content-imagecard-background, white);
                position: relative;
                height: 100%;
                width: 100%;
                color: var(--text-primary);
                display: grid;
                grid-template-columns: 1fr;
                grid-template-rows: 120px auto; /* rows will use full available height */
                grid-template-areas:
                    'image'
                    'content';
                transition: box-shadow 0.15s ease;
            }

            :host(:hover) {
                box-shadow: 0px 4px 14px 1px rgba(0, 0, 0, 0.25);
            }

            :host(:hover),
            :host(:hover) ::slotted(*) {
                cursor: pointer;
            }

            :host(:hover) #contentWrapper ::slotted(*) {
                background: transparent;
            }

            :host([noHover]:hover) {
                cursor: default;
                -webkit-box-shadow: none;
                box-shadow: none;
                transform: none;
            }

            :host([noHover]:hover) #contentWrapper ::slotted(*) {
                cursor: default;
            }

            #imageWrapper {
                grid-area: image;
                position: relative;
                display: block;
                width: 100%;
                height: 120px;
                background-color: var(--theme-primary);
                color: var(--text-light);
                filter: var(--sdb-content-imagecard-filter);
            }

            :host([secondary]) #imageWrapper,
            :host([secondary]) div.overlay {
                background-color: var(--theme-secondary);
            }

            :host([tertiary]) #imageWrapper,
            :host([tertiary]) div.overlay {
                background-color: var(--theme-tertiary);
            }

            section#contentWrapper {
                grid-area: content;
                display: flex;
                flex-flow: column wrap;
                justify-content: stretch;
                align-content: stretch;
                padding: 0;
            }

            .overlay {
                position: absolute;
                z-index: 1;
                width: 100%;
                height: 100%;
                background-color: var(--theme-tertiary);
                opacity: 0.7;
                top: 0;
                left: 0;
            }

            :host([noOverlay]) .overlay {
                display: none;
            }

            .image {
                position: relative;
                z-index: 2;
                display: grid;
                grid-template-columns: 1fr;
                height: 90px;
                padding: 15px 40px;
                align-items: center;
                justify-items: center;
            }

            :host section#imageWrapper .backgroundImage {
                background-repeat: no-repeat;
                background-size: cover;
                background-position: center center;
                -webkit-filter: grayscale(100%); /* Safari 6.0 - 9.0 */
                filter: grayscale(100%);
                display: block;
                width: 100%;
                height: 100%;
                position: absolute;
                top: 0;
                left: 0;
            }

            :host([noOverlay]) section#imageWrapper .backgroundImage {
                filter: none;
            }
        `,
];
__decorate([
    property({ type: String })
], SDBContentImageCard.prototype, "img", void 0);
__decorate([
    property({ type: String })
], SDBContentImageCard.prototype, "placeholder", void 0);
__decorate([
    property({ type: String })
], SDBContentImageCard.prototype, "background", void 0);
__decorate([
    property({ type: Boolean })
], SDBContentImageCard.prototype, "secondary", void 0);
__decorate([
    property({ type: Boolean })
], SDBContentImageCard.prototype, "tertiary", void 0);
__decorate([
    property({ type: Boolean })
], SDBContentImageCard.prototype, "hover", void 0);
__decorate([
    property({ type: Boolean })
], SDBContentImageCard.prototype, "noOverlay", void 0);
SDBContentImageCard = __decorate([
    customElement('sdb-content-imagecard')
], SDBContentImageCard);
export { SDBContentImageCard };
//# sourceMappingURL=sdb-content-imagecard.js.map